import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// #region agent log
try {
  fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TripStore.js:4',message:'TripStore initializing',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
} catch(e) {}
// #endregion

let storeInstance;
try {
  storeInstance = create(
    persist(
      (set, get) => ({
      // Trip setup data
      trip: {
        city: null,
        date: '',
        startTime: '',
        endTime: '',
        startLocation: null,
        endLocation: null,
      },

      // Places list
      places: [],

      // Settings
      settings: {
        optimizationMode: 'optimize_route', // or 'respect_priorities'
        travelMode: 'WALKING', // 'WALKING' | 'TRANSIT' | 'DRIVING' | 'BICYCLING'
      },

      // Generated itinerary
      itinerary: {
        planA: null,
        planB: null,
        activePlan: 'a',
      },

      // UI state
      ui: {
        currentStep: 'list', // 'setup' | 'list' | 'timeblocks' | 'itinerary'
        isGenerating: false,
      },

      // Mobile UI state
      mobileUI: {
        bottomSheetState: 'collapsed', // 'collapsed' | 'partial' | 'expanded'
        previewLocation: null, // Location being previewed on map
        activeSearchField: null, // 'start' | 'stops' | null
      },

      // Saved trips for "Previous Trips" feature
      savedTrips: [],

      // Navigation state
      navigation: {
        isActive: false, // Whether user is actively following the itinerary
        currentStopIndex: 0, // Index of the current stop being navigated to
        completedStops: [], // Array of stop indices that have been completed
        routeAlternatives: [], // Array of alternative routes for current leg
        selectedRouteIndex: 0, // Currently selected route
        showTripOverview: false, // Toggle to show full trip on map
      },

      // Actions
      setTrip: (tripData) => set({ trip: { ...get().trip, ...tripData } }),
      
      addPlace: (place) => {
        const newPlace = {
          id: `place_${Date.now()}_${Math.random()}`,
          ...place,
          priority: place.priority || 'want_to_see',
          lockedTime: place.lockedTime || null,
        };
        set({ places: [...get().places, newPlace] });
      },

      removePlace: (placeId) => {
        set({ places: get().places.filter(p => p.id !== placeId) });
      },

      updatePlace: (placeId, updates) => {
        set({
          places: get().places.map(p =>
            p.id === placeId ? { ...p, ...updates } : p
          ),
        });
      },

      reorderPlaces: (fromIndex, toIndex) => {
        const places = [...get().places];
        const [moved] = places.splice(fromIndex, 1);
        places.splice(toIndex, 0, moved);
        set({ places });
      },

      setOptimizationMode: (mode) => {
        set({
          settings: { ...get().settings, optimizationMode: mode },
        });
      },

      setTravelMode: (mode) => {
        set({
          settings: { ...get().settings, travelMode: mode },
        });
      },

      setItinerary: (itineraryData) => {
        set({
          itinerary: {
            planA: itineraryData.planA,
            planB: itineraryData.planB,
            activePlan: get().itinerary.activePlan || 'a',
          },
        });
      },

      setActivePlan: (plan) => {
        set({
          itinerary: { ...get().itinerary, activePlan: plan },
        });
      },

      setCurrentStep: (step) => {
        set({
          ui: { ...get().ui, currentStep: step },
        });
      },

      setIsGenerating: (isGenerating) => {
        set({
          ui: { ...get().ui, isGenerating },
        });
      },

      // Mobile UI actions
      setBottomSheetState: (state) => {
        set({
          mobileUI: { ...get().mobileUI, bottomSheetState: state },
        });
      },

      setPreviewLocation: (location) => {
        set({
          mobileUI: { ...get().mobileUI, previewLocation: location },
        });
      },

      setActiveSearchField: (field) => {
        set({
          mobileUI: { ...get().mobileUI, activeSearchField: field },
        });
      },

      // Saved trips actions
      saveCurrentTrip: () => {
        const state = get();
        if (!state.itinerary.planA && !state.itinerary.planB) return;

        const tripToSave = {
          id: `trip_${Date.now()}`,
          savedAt: new Date().toISOString(),
          trip: { ...state.trip },
          places: [...state.places],
          settings: { ...state.settings },
          itinerary: { ...state.itinerary },
        };

        set({
          savedTrips: [tripToSave, ...get().savedTrips].slice(0, 10), // Keep last 10 trips
        });
      },

      loadSavedTrip: (tripId) => {
        const savedTrip = get().savedTrips.find(t => t.id === tripId);
        if (!savedTrip) return;

        set({
          trip: savedTrip.trip,
          places: savedTrip.places,
          settings: savedTrip.settings,
          itinerary: savedTrip.itinerary,
          ui: { ...get().ui, currentStep: 'itinerary' },
        });
      },

      deleteSavedTrip: (tripId) => {
        set({
          savedTrips: get().savedTrips.filter(t => t.id !== tripId),
        });
      },

      // Navigation actions
      startNavigation: () => {
        set({
          navigation: {
            isActive: true,
            currentStopIndex: 0,
            completedStops: [],
            routeAlternatives: [],
            selectedRouteIndex: 0,
            showTripOverview: false,
          },
        });
      },

      stopNavigation: () => {
        set({
          navigation: {
            isActive: false,
            currentStopIndex: 0,
            completedStops: [],
            routeAlternatives: [],
            selectedRouteIndex: 0,
            showTripOverview: false,
          },
        });
      },

      setCurrentStopIndex: (index) => {
        set({
          navigation: {
            ...get().navigation,
            currentStopIndex: index,
          },
        });
      },

      completeStop: (stopIndex) => {
        const navigation = get().navigation;
        if (!navigation.completedStops.includes(stopIndex)) {
          set({
            navigation: {
              ...navigation,
              completedStops: [...navigation.completedStops, stopIndex],
            },
          });
        }
      },

      goToNextStop: () => {
        const navigation = get().navigation;
        const itinerary = get().itinerary;
        const activePlan = itinerary.activePlan === 'a' ? itinerary.planA : itinerary.planB;

        if (activePlan?.stops && navigation.currentStopIndex < activePlan.stops.length - 1) {
          // Mark current stop as completed
          const completedStops = navigation.completedStops.includes(navigation.currentStopIndex)
            ? navigation.completedStops
            : [...navigation.completedStops, navigation.currentStopIndex];

          set({
            navigation: {
              ...navigation,
              currentStopIndex: navigation.currentStopIndex + 1,
              completedStops: completedStops,
              routeAlternatives: [], // Clear routes when moving to next stop
              selectedRouteIndex: 0,
            },
          });
        }
      },

      setRouteAlternatives: (routes) => {
        set({
          navigation: {
            ...get().navigation,
            routeAlternatives: routes,
            selectedRouteIndex: 0,
          },
        });
      },

      selectRoute: (index) => {
        set({
          navigation: {
            ...get().navigation,
            selectedRouteIndex: index,
          },
        });
      },

      toggleTripOverview: () => {
        set({
          navigation: {
            ...get().navigation,
            showTripOverview: !get().navigation.showTripOverview,
          },
        });
      },

      reset: () => {
        set({
          trip: {
            city: null,
            date: '',
            startTime: '',
            endTime: '',
            startLocation: null,
            endLocation: null,
          },
          places: [],
          settings: {
            optimizationMode: 'optimize_route',
            travelMode: 'WALKING',
          },
          itinerary: {
            planA: null,
            planB: null,
            activePlan: 'a',
          },
          ui: {
            currentStep: 'list',
            isGenerating: false,
          },
          mobileUI: {
            bottomSheetState: 'collapsed',
            previewLocation: null,
            activeSearchField: null,
          },
          navigation: {
            isActive: false,
            currentStopIndex: 0,
            completedStops: [],
            routeAlternatives: [],
            selectedRouteIndex: 0,
            showTripOverview: false,
          },
        });
      },
    }),
    {
      name: 'whistlestop-trip-storage',
      partialize: (state) => ({
        trip: state.trip,
        places: state.places,
        settings: state.settings,
        itinerary: state.itinerary,
        ui: { currentStep: state.ui.currentStep },
        navigation: state.navigation,
        savedTrips: state.savedTrips,
      }),
    }
  )
  );
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TripStore.js:133',message:'TripStore created successfully',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
  // #endregion
} catch (error) {
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TripStore.js:136',message:'TripStore creation error',data:{error:error.message,stack:error.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
  // #endregion
  console.error('Error creating TripStore:', error);
  // Fallback store without persist
  storeInstance = create((set, get) => ({
    trip: { city: null, date: '', startTime: '', endTime: '', startLocation: null, endLocation: null },
    places: [],
    settings: { optimizationMode: 'optimize_route' },
    itinerary: { planA: null, planB: null, activePlan: 'a' },
    ui: { currentStep: 'list', isGenerating: false },
    currentStep: 'list',
    isGenerating: false,
    setTrip: (tripData) => set({ trip: { ...get().trip, ...tripData } }),
    addPlace: (place) => set({ places: [...get().places, { id: `place_${Date.now()}`, ...place, priority: place.priority || 'want_to_see' }] }),
    removePlace: (placeId) => set({ places: get().places.filter(p => p.id !== placeId) }),
    updatePlace: (placeId, updates) => set({ places: get().places.map(p => p.id === placeId ? { ...p, ...updates } : p) }),
    reorderPlaces: (fromIndex, toIndex) => {
      const places = [...get().places];
      const [moved] = places.splice(fromIndex, 1);
      places.splice(toIndex, 0, moved);
      set({ places });
    },
    setOptimizationMode: (mode) => set({ settings: { ...get().settings, optimizationMode: mode } }),
    setItinerary: (itineraryData) => set({ itinerary: { planA: itineraryData.planA, planB: itineraryData.planB, activePlan: get().itinerary.activePlan || 'a' } }),
    setActivePlan: (plan) => set({ itinerary: { ...get().itinerary, activePlan: plan } }),
    setCurrentStep: (step) => set({ ui: { ...get().ui, currentStep: step }, currentStep: step }),
    setIsGenerating: (isGenerating) => set({ ui: { ...get().ui, isGenerating }, isGenerating }),
    reset: () => set({ trip: { city: null, date: '', startTime: '', endTime: '', startLocation: null, endLocation: null }, places: [], settings: { optimizationMode: 'optimize_route' }, itinerary: { planA: null, planB: null, activePlan: 'a' }, ui: { currentStep: 'list', isGenerating: false }, currentStep: 'list', isGenerating: false }),
  }));
}

const useTripStore = storeInstance;
export default useTripStore;

